import moment from 'moment';
import 'moment/locale/nl';
import React, { useContext, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MemoryRouter, Redirect, Route, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { WithFundsSelection } from '../../contexts/funds-robot/FundsSelectionContext';
import { ComparisonScreen } from './comparison-screen/ComparisonScreen';
import { FundsSelectionScreen, IntermediateFundsSelectionContext } from './funds-selection-screen/FundsSelectionScreen';
import { FundsQueryContext, WithFundsQuery } from '../../contexts/funds-robot/FundsQueryContext';
import { WithFundRates } from '../../contexts/funds-robot/FundRatesContext';
import { WithChartInterval } from '../../contexts/funds-robot/ChartIntervalContext';
import { SwitchWithPageTracking } from '../../utils/hooks';
import { ROBOT_ENVIRONMENT } from '../../config/constants';
import { WithRobotEnvironment } from '../../contexts/general/RobotEnvironmentContext';

// This redundant import needs to stay here, otherwise I18N wont work!
import { prepareHostSite } from '../../utils/prepareBNDwebsite';

moment.locale('nl');

const queryClient = new QueryClient();

export const FundsRobot: React.FC<{ environment: ROBOT_ENVIRONMENT }> = ({ environment }) => {
    // Prepare the marketing site for the display of this tool
    useEffect(() => {
        prepareHostSite();
    }, []);

    return (
        <div className=" text-bnd-black funds-robot text-base leading-normal font-light min-h-screen text-left mx-force-edge lg:px-4">
            <div className="max-w-screen-3xl mx-auto">
                <QueryClientProvider client={queryClient}>
                    <WithRobotEnvironment environment={environment}>
                        <WithChartInterval>
                            <WithFundsQuery>
                                <WithFundsSelection syncToUrl={true}>
                                    <WithFundsSelection context={IntermediateFundsSelectionContext}>
                                        <WithFundRates>
                                            <MemoryRouter>
                                                <SwitchWithPageTracking>
                                                    <Route exact path="/selection">
                                                        <FundsSelectionScreenRoute />
                                                    </Route>
                                                    <Route exact path="/comparison">
                                                        <ComparisonScreen />
                                                    </Route>
                                                    <Route path="/">
                                                        <Redirect to="/selection" />
                                                    </Route>
                                                </SwitchWithPageTracking>
                                            </MemoryRouter>
                                        </WithFundRates>
                                    </WithFundsSelection>
                                </WithFundsSelection>
                            </WithFundsQuery>
                        </WithChartInterval>
                    </WithRobotEnvironment>
                </QueryClientProvider>
            </div>
        </div>
    );
};

const FundsSelectionScreenRoute: React.FC<{}> = () => {
    const fundsQueryContext = useContext(FundsQueryContext);
    const history = useHistory();

    // Retreive selection from search params. If existing, continue to comparison
    useEffect(() => {
        const currentParams = new URLSearchParams(window.location.search);
        const fundIds = currentParams.getAll('fundId');
        if (fundIds.length > 0) {
            history.push('/comparison');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <FundsSelectionScreen
            titleBox={<TitleBox />}
            sections={[
                {
                    fundCategory: 'Portfolio',
                    funds: fundsQueryContext.assets.all.filter((asset) => asset.category === 'Portfolio'),
                },

                {
                    fundCategory: 'Stock',
                    funds: fundsQueryContext.assets.all.filter((asset) => asset.category === 'Stock'),
                },
                {
                    fundCategory: 'Bond',
                    funds: fundsQueryContext.assets.all.filter((asset) => asset.category === 'Bond'),
                },
            ]}
        />
    );
};

const TitleBox: React.FC<{}> = () => {
    const { t } = useTranslation('funds');

    return <p className={'text-lg max-w-screen-md text-center'}>{t('funds-selection-screen-subtitle')}</p>;
};
